import type { HitowaMenuDefinition } from './types';
import { permissionsOf } from '../../../constants/permissions';

/**
 * The Hitowa sidebar — transcribed from
 * `ConceptDesigns/FCオーナー/shell/rah-shell.js` (MENU.ippan / MENU.kanri).
 *
 * Labels, icons, ordering and the 一般 / 管理 split follow the ConceptDesign
 * exactly (MN-02 / MN-03). MN-02 is explicit that the tabs organise by
 * function, not by role: one tree serves every account and `permissions` decide
 * what each of them sees.
 *
 * `path` points at the RA2N route when the screen already exists; everything
 * else is `disabled` until its screen is built, so this file is also the
 * remaining scope.
 */
export const hitowaMenu: HitowaMenuDefinition = {
  tabs: [
    {
      id: 'ippan',
      label: '一般',
      groups: [
        {
          id: 'dash',
          icon: '📊',
          label: 'ダッシュ',
          path: '/',
        },
        {
          id: 'riyou',
          permissions: permissionsOf('利用者管理'),
          icon: '📂',
          label: '利用者',
          items: [
            { icon: '👤', label: '利用者管理', path: '/patient-management' },
            // RA2N's own consent screen, which it lists as 加療期間期限切れリスト.
            // The label stays the ConceptDesign's; the URL is the base one.
            { icon: '📋', label: '同意書管理', path: '/consents/treatment_period' },
          ],
        },
        {
          id: 'sched',
          permissions: permissionsOf('スケジュール・実績管理'),
          icon: '📅',
          label: '予定実績',
          items: [
            { section: '予定' },
            { icon: '📅', label: 'スケジュール', path: '/visits/schedule' },
            { icon: '👥', label: '利用者予定', path: '/visits/patient_init' },
            { icon: '🔒', label: '予定確定', path: '/schedule/lock', disabled: true },
            { section: '実績' },
            { icon: '📊', label: '実績管理', path: '/results', disabled: true },
            { icon: '📝', label: '実績(施術者)', path: '/results/therapist', disabled: true },
            { icon: '📈', label: '実績(利用者)', path: '/results/user', disabled: true },
            { icon: '📋', label: '実績一覧表', path: '/results/list', disabled: true },
            { icon: '✅', label: '月次確定処理', path: '/results/confirm', disabled: true },
            { icon: '🔏', label: '月次確定', path: '/results/monthly-confirm', disabled: true },
          ],
        },
        {
          id: 'rezept',
          permissions: permissionsOf('レセプト管理'),
          icon: '🧾',
          label: 'レセプト',
          items: [
            { icon: '🧾', label: 'レセプト処理', path: '/rezepts/init' },
            { icon: '🖨', label: 'レセプト印刷', path: '/rezepts/print', disabled: true },
            { icon: '📄', label: '書類セット', path: '/documents/set', disabled: true },
          ],
        },
        {
          id: 'print',
          permissions: permissionsOf('書式マスタ', 'タックシール・封筒印刷', '売上管理'),
          icon: '🖨',
          label: '帳票',
          items: [
            { icon: '📝', label: '申請書', path: '/reports/application-form', disabled: true },
            { icon: '💴', label: '請求書', path: '/rezepts/invoices_init' },
            { icon: '🧾', label: '領収書', path: '/rezepts/visit_clinic_invoices_init' },
            { icon: '🚗', label: '往療費内訳書', path: '/reports/home-visit-fee', disabled: true },
            {
              icon: '📊',
              label: '利用者月間実績',
              path: '/reports/user-monthly-results',
              disabled: true,
            },
            {
              icon: '📤',
              label: '報告書CSV',
              path: '/reports/treatment-report-csv',
              disabled: true,
            },
          ],
        },
        {
          id: 'pay',
          permissions: permissionsOf('法人請求管理', '加盟店請求管理'),
          icon: '💴',
          label: '精算入金',
          items: [
            { icon: '🧮', label: '精算明細確認', path: '/settlements/view', disabled: true },
            { icon: '💳', label: '入金状況', path: '/payments/status', disabled: true },
          ],
        },
      ],
    },
    {
      id: 'kanri',
      label: '管理',
      groups: [
        {
          id: 'fmaster',
          permissions: permissionsOf(
            '施術家マスタ',
            '医師マスタ',
            '医療機関マスタ',
            '事業所リスト'
          ),
          icon: '🧑‍⚕',
          label: '現場マスタ',
          items: [
            { icon: '🧑‍⚕', label: '施術者管理', path: '/massagers/init' },
            { icon: '🩺', label: '医師管理', path: '/doctors/init', disabled: true },
            { icon: '👩‍⚕', label: 'ケアマネ管理', path: '/care-managers/init', disabled: true },
            { icon: '🏥', label: '施設管理', path: '/hospitals/init' },
            {
              icon: '🏢',
              label: '事業所管理',
              path: '/business-destinations/init',
              disabled: true,
            },
          ],
        },
        {
          id: 'clinic',
          permissions: permissionsOf('拠点マスタ'),
          icon: '🔁',
          label: '拠点切替',
          path: '/clinic-switch',
          disabled: true,
        },
      ],
    },
  ],
};

export default hitowaMenu;
